import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On a 401, try once to exchange the refresh token for a new access token and
// replay the original request. If that fails, clear the session and bounce to
// login.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url: string = original.url || '';
    const isAuthCall =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/google') ||
      url.includes('/auth/refresh');

    if (status === 401 && !original._retry && !isAuthCall) {
      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) {
        return Promise.reject(error);
      }
      original._retry = true;
      try {
        // Use a bare axios call so this request skips the interceptor.
        const resp = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refresh,
        });
        localStorage.setItem('token', resp.data.access_token);
        localStorage.setItem('refresh_token', resp.data.refresh_token);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${resp.data.access_token}`;
        return api(original);
      } catch (refreshError) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  email: string;
  full_name: string;
  phone?: string;
  handicap?: number;
  home_club?: string;
  avatar_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  x_url?: string;
  tiktok_url?: string;
  is_active: boolean;
  is_verified: boolean;
  is_admin?: boolean;
}

export interface AdminWithdrawal {
  id: number;
  amount: number;
  amount_zar: number;
  status: string;
  reference?: string;
  notes?: string;
  requested_at: string;
  cooling_off_ends?: string;
  processed_at?: string;
  user_id: number;
  user_name: string;
  user_email: string;
  bank_account_masked: string;
}

export interface Game {
  id: number;
  name: string;
  course_name: string;
  scheduled_date: string;
  scoring_format: string;
  holes?: number;
  status: string;
  created_by: number;
  player_count: number;
  created_at: string;
  invite_code?: string;
}

export interface Course {
  id: number;
  name: string;
  description?: string;
  address: string;
  city: string;
  province: string;
  latitude: number;
  longitude: number;
  holes: number;
  par: number;
  length_meters?: number;
  slope_rating?: number;
  course_rating?: number;
  has_driving_range: boolean;
  has_pro_shop: boolean;
  has_restaurant: boolean;
  has_accommodation: boolean;
  has_carts: boolean;
  has_rental_clubs: boolean;
  green_fee_weekday?: number;
  green_fee_weekend?: number;
  cart_fee?: number;
  phone?: string;
  email?: string;
  website?: string;
  is_active: boolean;
  is_verified: boolean;
  distance_km?: number;
}

export interface Bet {
  id: number;
  game_id: number;
  bet_type: string;
  amount_per_player: number;
  total_pool: number;
  status: string;
  winner_id?: number;
  hole_number?: number;
  label?: string;
  participant_count: number;
  participant_ids?: number[];
  declined_ids?: number[];
  created_by?: number;
  created_at: string;
}

export interface SettlementShare {
  player_id: number;
  amount_cents: number;
}

export interface SettlementApproval {
  player_id: number;
  approved: boolean;
  voted_at?: string;
}

export interface Settlement {
  id: number;
  bet_id: number;
  proposed_by: number;
  status: string;
  shares: SettlementShare[];
  approvals: SettlementApproval[];
  created_at?: string;
  resolved_at?: string;
}

export interface Wallet {
  id: number;
  balance: number;
  balance_zar: number;
}

export interface Transaction {
  id: number;
  transaction_type: string;
  amount: number;
  amount_zar: number;
  status: string;
  description?: string;
  payment_method: string;
  reference?: string | null;
  completed_at?: string | null;
  created_at: string;
}

export interface WithdrawalRequest {
  id: number;
  amount: number;
  amount_zar: number;
  status: string;
  reference?: string | null;
  notes?: string | null;
  requested_at: string;
  cooling_off_ends?: string | null;
  processed_at?: string | null;
  days_until_ready?: number | null;
}

export interface BankDetailsInput {
  account_holder: string;
  bank: string;
  branch_code: string;
  account_number: string;
  account_type: string;
}

export interface SavedBankDetails extends BankDetailsInput {
  has_details: boolean;
}

export interface CommunityUser {
  id: number;
  full_name: string;
  handicap?: number;
  home_club?: string;
  avatar_url?: string;
  is_in_circle?: boolean;
}

export interface PublicGolferProfile {
  id: number;
  full_name: string;
  handicap?: number;
  home_club?: string;
  avatar_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  x_url?: string;
  tiktok_url?: string;
  is_in_circle: boolean;
}

export interface MyStats {
  games_total: number;
  games_completed: number;
  wins: number;
  win_rate: number;
  current_win_streak: number;
  best_round_strokes?: number | null;
  worst_round_strokes?: number | null;
  total_winnings: number;
  total_winnings_zar: number;
  total_staked: number;
  total_staked_zar: number;
  net_profit: number;
  net_profit_zar: number;
  biggest_win: number;
  biggest_win_zar: number;
}

export const authApi = {
  register: async (data: { email: string; password: string; full_name: string; handicap?: number }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  login: async (email: string, password: string) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    const response = await api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },
  refresh: async (refreshToken: string) => {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
  googleLogin: async (credential: string) => {
    const response = await api.post('/auth/google', { credential });
    return response.data;
  },
  requestPasswordReset: async (email: string) => {
    const response = await api.post('/auth/password-reset/request', { email });
    return response.data;
  },
  confirmPasswordReset: async (token: string, new_password: string) => {
    const response = await api.post('/auth/password-reset/confirm', { token, new_password });
    return response.data;
  },
  requestEmailVerification: async () => {
    const response = await api.post('/auth/verify-email/request');
    return response.data;
  },
  confirmEmailVerification: async (token: string) => {
    const response = await api.post('/auth/verify-email/confirm', { token });
    return response.data;
  },
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Best effort - clear local state regardless.
    }
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  updateMe: async (data: Partial<User>) => {
    const response = await api.patch('/auth/me', data);
    return response.data;
  },
  uploadAvatar: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/auth/me/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const walletApi = {
  getWallet: async (): Promise<Wallet> => {
    const response = await api.get('/wallet/');
    return response.data;
  },
  
  // PayFast deposit. `amountRands` is in Rands; the API works in cents.
  createPayFastDeposit: async (amountRands: number) => {
    const response = await api.post('/wallet/deposit/payfast', {
      amount: Math.round(amountRands * 100),
      payment_method: 'payfast'
    });
    return response.data;
  },
  
  // Faux/sandbox gateway completion (testing only; backend must have FAUX_PAYMENTS on)
  completeFauxDeposit: async (reference: string) => {
    const response = await api.post('/wallet/deposit/faux/complete', { reference });
    return response.data;
  },

  // Withdrawal requests. Bank details come from the user's saved payout
  // account (set up in Profile), so none are passed here.
  createWithdrawalRequest: async (amount: number, notes?: string) => {
    const response = await api.post('/wallet/withdrawal/request', { amount, notes });
    return response.data;
  },

  getWithdrawalRequests: async (): Promise<WithdrawalRequest[]> => {
    const response = await api.get('/wallet/withdrawal/requests');
    return response.data;
  },

  // The caller's saved structured payout bank details.
  getSavedBankDetails: async (): Promise<SavedBankDetails> => {
    const response = await api.get('/wallet/bank-details');
    return response.data;
  },

  // Save/update the caller's structured payout bank details.
  saveBankDetails: async (details: BankDetailsInput): Promise<SavedBankDetails> => {
    const response = await api.put('/wallet/bank-details', details);
    return response.data;
  },

  cancelWithdrawal: async (withdrawalId: number): Promise<WithdrawalRequest> => {
    const response = await api.post(`/wallet/withdrawal/${withdrawalId}/cancel`);
    return response.data;
  },
  
  getTransactions: async (): Promise<Transaction[]> => {
    const response = await api.get('/wallet/transactions');
    return response.data;
  },
};

export interface AdminOverview {
  users_total: number; users_verified: number;
  games_total: number; games_in_progress: number; games_completed: number; games_disputed: number;
  revenue_zar: number; deposits_zar: number; withdrawals_paid_zar: number;
  wallet_liability_zar: number; pending_withdrawals_count: number; pending_withdrawals_zar: number;
}

export interface AdminReconciliation {
  deposits_zar: number; fees_zar: number; withdrawals_paid_zar: number;
  wallet_balances_zar: number; reserved_in_open_bets_zar: number;
  reserved_in_pending_withdrawals_zar: number;
  expected_held_zar: number; actual_held_zar: number; discrepancy_zar: number; balanced: boolean;
}

export interface AdminLedgerRow {
  user_id: number; user_name: string; user_email: string;
  stored_balance_zar: number; ledger_balance_zar: number; difference_zar: number;
}

export interface AdminLedgerReport {
  wallets_checked: number; mismatched_count: number;
  total_difference_zar: number; mismatches: AdminLedgerRow[];
}

export interface AdminAuditEntry {
  id: number; created_at: string; actor_id?: number | null; actor_email?: string | null;
  action: string; target_type?: string | null; target_id?: string | null; detail?: string | null;
}

export interface AdminUser {
  id: number; full_name: string; email: string; is_verified: boolean; is_admin: boolean;
  handicap?: number; created_at?: string; balance_zar: number; games_count: number;
}

export interface AdminTxn {
  id: number; type: string; amount_zar: number; status: string;
  description?: string | null; created_at?: string | null;
}

export interface AdminUserDetail {
  id: number; full_name: string; email: string; phone?: string | null;
  handicap?: number | null; home_club?: string | null; avatar_url?: string | null;
  is_verified: boolean; is_admin: boolean; created_at?: string | null;
  balance_zar: number; lifetime_deposits_zar: number; lifetime_withdrawals_paid_zar: number;
  lifetime_fees_zar: number; reserved_in_open_bets_zar: number;
  pending_withdrawals_zar: number; pending_withdrawals_count: number;
  games_played: number; bets_won_count: number; bank_account_masked: string;
  recent_transactions: AdminTxn[];
}

export interface AdminDispute {
  id: number; name: string; course_name: string; scheduled_date: string; player_count: number;
}

// Admin-only. Every endpoint is enforced server-side by the admin gate.
export const adminApi = {
  getOverview: async (): Promise<AdminOverview> => {
    const response = await api.get('/admin/overview');
    return response.data;
  },
  getReconciliation: async (): Promise<AdminReconciliation> => {
    const response = await api.get('/admin/reconcile');
    return response.data;
  },
  getLedger: async (): Promise<AdminLedgerReport> => {
    const response = await api.get('/admin/reconcile/ledger');
    return response.data;
  },
  getAudit: async (params?: { action?: string; target_type?: string; target_id?: string; limit?: number }): Promise<AdminAuditEntry[]> => {
    const response = await api.get('/admin/audit', { params });
    return response.data;
  },
  listUsers: async (q?: string): Promise<AdminUser[]> => {
    const response = await api.get('/admin/users', { params: q ? { q } : {} });
    return response.data;
  },
  getUser: async (userId: number): Promise<AdminUserDetail> => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },
  setUserVerified: async (userId: number, isVerified: boolean) => {
    const response = await api.post(`/admin/users/${userId}/verify`, { is_verified: isVerified });
    return response.data;
  },
  setUserAdmin: async (userId: number, isAdmin: boolean) => {
    const response = await api.post(`/admin/users/${userId}/set-admin`, { is_admin: isAdmin });
    return response.data;
  },
  resendVerification: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/resend-verification`);
    return response.data;
  },
  emailUser: async (userId: number, subject: string, body: string): Promise<{ message: string; delivered: boolean }> => {
    const response = await api.post(`/admin/users/${userId}/email`, { subject, body });
    return response.data;
  },
  listDisputes: async (): Promise<AdminDispute[]> => {
    const response = await api.get('/admin/disputes');
    return response.data;
  },
  // Resolve a dispute: reopen for correction, or accept the scores as final.
  reopenGame: async (gameId: number) => {
    const response = await api.post(`/admin/games/${gameId}/reopen`);
    return response.data;
  },
  approveGameResults: async (gameId: number) => {
    const response = await api.post(`/admin/games/${gameId}/approve`);
    return response.data;
  },
  listWithdrawals: async (): Promise<AdminWithdrawal[]> => {
    const response = await api.get('/wallet/admin/withdrawals');
    return response.data;
  },
  // Full bank details — this call is audit-logged on the server.
  revealBankDetails: async (withdrawalId: number): Promise<{ withdrawal_id: number; bank_account: string }> => {
    const response = await api.get(`/wallet/admin/withdrawals/${withdrawalId}/bank-details`);
    return response.data;
  },
  releaseWithdrawal: async (withdrawalId: number): Promise<AdminWithdrawal> => {
    const response = await api.post(`/wallet/admin/withdrawals/${withdrawalId}/release`);
    return response.data;
  },
  approveWithdrawal: async (withdrawalId: number): Promise<AdminWithdrawal> => {
    const response = await api.post(`/wallet/admin/withdrawals/${withdrawalId}/approve`);
    return response.data;
  },
  completeWithdrawal: async (withdrawalId: number): Promise<AdminWithdrawal> => {
    const response = await api.post(`/wallet/admin/withdrawals/${withdrawalId}/complete`);
    return response.data;
  },
};

export const gamesApi = {
  getMyGames: async (): Promise<Game[]> => {
    const response = await api.get('/games/');
    return response.data;
  },
  getGame: async (gameId: number) => {
    const response = await api.get(`/games/${gameId}`);
    return response.data;
  },
  createGame: async (data: { name: string; course_name: string; scheduled_date: string; scoring_format: string; holes?: number; player_emails: string[]; player_ids?: number[] }) => {
    const response = await api.post('/games/', data);
    return response.data;
  },
  acceptInvite: async (gameId: number) => {
    const response = await api.post(`/games/${gameId}/accept`);
    return response.data;
  },
  // Share/WhatsApp invite links
  getInvitePreview: async (code: string): Promise<{
    name: string; course_name: string; scheduled_date: string; holes: number;
    status: string; creator_name: string; player_count: number; is_full: boolean; expired: boolean;
  }> => {
    const response = await api.get(`/games/invite/${code}`);
    return response.data;
  },
  joinByInvite: async (code: string): Promise<{ status: 'joined' | 'pending'; game_id?: number; message: string }> => {
    const response = await api.post(`/games/invite/${code}/join`);
    return response.data;
  },
  // Host confirmation of link joiners
  getJoinRequests: async (gameId: number): Promise<{ id: number; user_id: number; user_name: string; handicap?: number; created_at: string }[]> => {
    const response = await api.get(`/games/${gameId}/join-requests`);
    return response.data;
  },
  approveJoinRequest: async (gameId: number, requestId: number) => {
    const response = await api.post(`/games/${gameId}/join-requests/${requestId}/approve`);
    return response.data;
  },
  declineJoinRequest: async (gameId: number, requestId: number) => {
    const response = await api.post(`/games/${gameId}/join-requests/${requestId}/decline`);
    return response.data;
  },
  addPlayer: async (gameId: number, data: { user_id?: number; email?: string; phone?: string }) => {
    const response = await api.post(`/games/${gameId}/players`, data);
    return response.data;
  },
  removePlayer: async (gameId: number, playerId: number) => {
    const response = await api.delete(`/games/${gameId}/players/${playerId}`);
    return response.data;
  },
  deleteGame: async (gameId: number) => {
    const response = await api.delete(`/games/${gameId}`);
    return response.data;
  },
  startGame: async (gameId: number) => {
    const response = await api.post(`/games/${gameId}/start`);
    return response.data;
  },
  submitScore: async (gameId: number, holeNumber: number, strokes: number) => {
    const response = await api.post(`/games/${gameId}/scores`, { hole_number: holeNumber, strokes });
    return response.data;
  },
  completeGame: async (gameId: number) => {
    const response = await api.post(`/games/${gameId}/complete`);
    return response.data;
  },
  reopenGame: async (gameId: number) => {
    const response = await api.post(`/games/${gameId}/reopen`);
    return response.data;
  },
  getVerification: async (gameId: number) => {
    const response = await api.get(`/games/${gameId}/verification`);
    return response.data;
  },
  verifyResults: async (gameId: number, status: 'approved' | 'disputed', disputeReason?: string) => {
    const response = await api.post(`/games/${gameId}/verification`, {
      status,
      dispute_reason: disputeReason,
    });
    return response.data;
  },
};

export const coursesApi = {
  getCourses: async (params?: {
    search?: string;
    city?: string;
    province?: string;
    lat?: number;
    lng?: number;
    radius_km?: number;
    limit?: number;
    offset?: number;
  }): Promise<Course[]> => {
    const response = await api.get('/courses/', { params });
    return response.data;
  },
  getNearbyCourses: async (lat: number, lng: number, radius_km: number = 50, limit: number = 10): Promise<Course[]> => {
    const response = await api.get('/courses/nearby', {
      params: { lat, lng, radius_km, limit }
    });
    return response.data;
  },
  getCourse: async (courseId: number): Promise<Course> => {
    const response = await api.get(`/courses/${courseId}`);
    return response.data;
  },
  getCities: async (): Promise<string[]> => {
    const response = await api.get('/courses/cities');
    return response.data;
  },
  getProvinces: async (): Promise<string[]> => {
    const response = await api.get('/courses/provinces');
    return response.data;
  },
};

export const betsApi = {
  getGameBets: async (gameId: number): Promise<Bet[]> => {
    const response = await api.get(`/bets/game/${gameId}`);
    return response.data;
  },
  createBet: async (data: {
    game_id: number;
    bet_type: string;
    amount_per_player: number;
    hole_number?: number;
    label?: string;
  }) => {
    const response = await api.post('/bets/', data);
    return response.data;
  },
  joinBet: async (betId: number) => {
    const response = await api.post(`/bets/${betId}/join`);
    return response.data;
  },
  declineBet: async (betId: number) => {
    const response = await api.post(`/bets/${betId}/decline`);
    return response.data;
  },
  // Delete an unsettled bet; refunds every participant's reserved stake.
  deleteBet: async (betId: number) => {
    const response = await api.delete(`/bets/${betId}`);
    return response.data;
  },
  settleBet: async (betId: number) => {
    const response = await api.post(`/bets/${betId}/settle`);
    return response.data;
  },
  getSettlement: async (betId: number): Promise<{ settlement: Settlement | null }> => {
    const response = await api.get(`/bets/${betId}/settlement`);
    return response.data;
  },
  getGameSettlements: async (gameId: number): Promise<{ settlements: Record<string, Settlement | null> }> => {
    const response = await api.get(`/bets/game/${gameId}/settlements`);
    return response.data;
  },
  proposeSettlement: async (betId: number, shares: SettlementShare[]) => {
    const response = await api.post(`/bets/${betId}/settlement/propose`, { shares });
    return response.data;
  },
  voteSettlement: async (betId: number, settlementId: number, approve: boolean) => {
    const response = await api.post(`/bets/${betId}/settlement/${settlementId}/vote`, { approve });
    return response.data;
  },
  finalizeSettlement: async (betId: number, settlementId: number) => {
    const response = await api.post(`/bets/${betId}/settlement/${settlementId}/finalize`);
    return response.data;
  },
};

export const communityApi = {
  searchUsers: async (q: string, limit: number = 20): Promise<CommunityUser[]> => {
    const response = await api.get('/community/search', { params: { q, limit } });
    return response.data;
  },
  getSuggestions: async (limit: number = 10): Promise<CommunityUser[]> => {
    const response = await api.get('/community/suggestions', { params: { limit } });
    return response.data;
  },
  getMyCircle: async (): Promise<CommunityUser[]> => {
    const response = await api.get('/community/mine');
    return response.data;
  },
  getUserProfile: async (userId: number): Promise<PublicGolferProfile> => {
    const response = await api.get(`/community/user/${userId}`);
    return response.data;
  },
  followUser: async (userId: number) => {
    const response = await api.post(`/community/follow/${userId}`);
    return response.data;
  },
  unfollowUser: async (userId: number) => {
    const response = await api.delete(`/community/follow/${userId}`);
    return response.data;
  },
};

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  body?: string;
  link?: string;
  is_read: boolean;
  created_at: string;
}

export const notificationsApi = {
  list: async (): Promise<AppNotification[]> => {
    const response = await api.get('/notifications/');
    return response.data;
  },
  unreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread_count');
    return response.data.count;
  },
  markAllRead: async () => {
    const response = await api.post('/notifications/read');
    return response.data;
  },
};

export const statsApi = {
  getMyStats: async (): Promise<MyStats> => {
    const response = await api.get('/stats/me');
    return response.data;
  },
  getUserStats: async (userId: number): Promise<MyStats> => {
    const response = await api.get(`/stats/user/${userId}`);
    return response.data;
  },
};

export interface ThreadUser {
  id: number;
  full_name: string;
  handicap?: number;
  home_club?: string;
  avatar_url?: string;
}

export interface ThreadSummary {
  thread_id: number;
  other_user: ThreadUser;
  last_message?: string | null;
  last_message_at?: string | null;
}

export interface ChatMessage {
  id: number;
  thread_id: number;
  sender_id: number;
  body: string;
  created_at: string;
}

export const messagingApi = {
  listThreads: async (): Promise<ThreadSummary[]> => {
    const response = await api.get('/messages/threads');
    return response.data;
  },
  getOrCreateThread: async (userId: number): Promise<{ thread_id: number }> => {
    const response = await api.post(`/messages/thread/${userId}`);
    return response.data;
  },
  getThread: async (threadId: number): Promise<ChatMessage[]> => {
    const response = await api.get(`/messages/thread/${threadId}`);
    return response.data;
  },
  sendMessage: async (threadId: number, body: string): Promise<ChatMessage> => {
    const response = await api.post(`/messages/thread/${threadId}/messages`, { body });
    return response.data;
  },
  deleteMessage: async (threadId: number, messageId: number) => {
    const response = await api.delete(`/messages/thread/${threadId}/messages/${messageId}`);
    return response.data;
  },
};

export default api;
